import React, { useEffect, useRef, useState } from 'react';
import {
  Link, Location, UNSAFE_LocationContext as LocationContext, useLocation, useNavigationType, useOutlet,
} from 'react-router-dom';
import {
  Box, Drawer, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { useAuthStore } from '../../store/authStore';
import AccessService from '../../api/services/access.service';
import { NAV_SECTIONS, sectionForPath, NavSection } from './navConfig';
import { PanelSlotProvider } from './NavPanel';
import { SectionActiveContext } from './SectionActive';
import { BreadcrumbProvider } from './Breadcrumb';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import ErrorBoundary from '../ui/ErrorBoundary';
import { boundaryKey } from './boundaryKey';
import Topbar from './Topbar';
import { layout, nav } from '../../theme';

// The shell: one nav unit in two panes, then a topbar, then the page.
//
//   ┌─────────────────────────────────────────────────────┐
//   │ logo            breadcrumb        company   avatar   │ 56px
//   ├──────┬──────────────────┬───────────────────────────┤
//   │ dark │  contextual      │                           │
//   │ rail │  panel — owned   │  page                     │
//   │ app  │  by the section  │                           │
//   └──────┴──────────────────┴───────────────────────────┘
//    68px         232px                  rest
//
// The rail is the app and never changes. The panel belongs to whichever
// section you are in and is filled by the page through <NavPanel>. Files'
// vault rail is that panel rather than a second sidebar next to the first,
// which is what made /files 472px of chrome before.
//
// Nothing here collapses. A sidebar that can be collapsed has to be styled
// twice, and the old one hid the logout button and every nav label in its
// collapsed state — the icons had no tooltips, so collapsed nav was a
// column of unlabelled glyphs.
//
// The section's name is deliberately NOT repeated in the panel or the
// topbar. It was in four places at once on /files: the rail label, a panel
// heading, a breadcrumb and the page's own h1. The rail says which section
// you are in; the page's h1 names the screen; the breadcrumb appears only
// when there is a level to climb back to.
//
// **On a phone the two nav panes become one drawer.** 68 + 232 is 300px of
// chrome, which is most of a 390px screen — and the panel was already
// hidden below `md`, so a phone lost the vault's own navigation entirely:
// no rosters, no categories, no recently-deleted, with nothing to open
// them from. The drawer carries BOTH, in the same order, so nothing is
// unreachable and there is no second information architecture to learn.
//
// The panel's content is a portal (see NavPanel), which is what makes this
// cheap: exactly one slot exists, and it is mounted either in the desktop
// aside or in the drawer. No page knows which.

// Every section's last page stays mounted, hidden, so leaving a section
// to look something up and coming back finds it exactly as it was: the
// week and the person open in Payroll, the candidate in Hiring, the
// filters, the scroll. Each page scrolls in its own layer, so its scroll
// position survives too (display: none would lose it). The rail takes you
// back to where you were in a section; the section you are already in
// takes you to its start.
//
// A hidden page keeps reading the location it was left at, so it does not
// react to the other section's URL. Switching company reloads the app, so
// nothing kept here crosses companies.
interface KeptPage { element: React.ReactNode; location: Location; boundary: string }

const AppShell: React.FC = () => {

  const location = useLocation();
  const { pathname } = location;
  const navigationType = useNavigationType();
  const outlet = useOutlet();
  const { account, hasPermission, canArea, setAccess } = useAuthStore();

  // What this person may open, from the server. Fetched per account; until
  // it lands (or if the server predates it) the role's defaults stand in.
  useEffect(() => {
    if (!account?._id || account.role === 'SA') return undefined;
    let cancelled = false;
    AccessService.me()
      .then((res) => { if (!cancelled && res?.access) setAccess(res.access); })
      .catch(() => { /* older server, or offline: role defaults keep standing in */ });
    return () => { cancelled = true; };
  }, [account?._id, account?.role, setAccess]);
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const [navOpen, setNavOpen] = useState(false);

  // Navigating closes it. Without this, tapping a section leaves the drawer
  // sitting over the page you just asked for.
  useEffect(() => { setNavOpen(false); }, [pathname]);

  const active = sectionForPath(pathname);
  const current = active?.to || pathname;
  const kept = useRef(new Map<string, KeptPage>());
  kept.current.set(current, { element: outlet, location, boundary: boundaryKey(pathname) });
  // The rail goes back to where you were in another section.
  const hrefFor = (section: NavSection) => {
    if (section.to === active?.to) return section.to;
    const page = kept.current.get(section.to);
    return page ? `${page.location.pathname}${page.location.search}` : section.to;
  };
  // Makes navConfig's promise true: the tab names the section you are in.
  useDocumentTitle(active?.label);

  const visible = NAV_SECTIONS.filter((s: NavSection) => {
    if (s.hidden) return false;
    if (s.systemAdminOnly) return account?.role === 'SA';
    if (s.areas) return s.areas.some((a) => canArea(a));
    if (!s.permission) return true;
    return hasPermission(s.permission);
  });

  return (
    <BreadcrumbProvider>
      <PanelSlotProvider>
        {(setSlot) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            {/* The bar runs the full width and the nav hangs beneath it, so
                the logo has somewhere to live and the rail and panel start
                on the same line as the page. */}
            <Topbar onOpenNav={isPhone ? () => setNavOpen(true) : undefined} />

            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {/* ---------------------------------------------------- rail */}
            {/* Not rendered at all on a phone rather than hidden with CSS:
                the drawer already carries these links, and two copies in
                the DOM is a duplicate <nav> for a screen reader. */}
            {!isPhone && (
            <Box
              component="nav"
              aria-label="Sections"
              sx={{
                width: layout.railWidth,
                flexShrink: 0,
                bgcolor: nav.bgRail,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                py: 2,
                overflow: 'hidden',
              }}
            >
              {visible.map((section) => {
                const isActive = active?.to === section.to;
                // No tooltip. It only repeated the label printed under the
                // icon, and it opened to the RIGHT — straight over the
                // contextual panel. MUI tooltips catch the pointer, so the
                // one left behind by the section you just clicked sat on
                // the panel's first rows and swallowed half of each button.
                return (
                    <Box
                      key={section.to}
                      component={Link}
                      to={hrefFor(section)}
                      aria-current={isActive ? 'page' : undefined}
                      sx={{
                        position: 'relative',
                        // Full rail width. The highlight used to be a 52px
                        // pill floating inside a 68px column, which left an
                        // uneven dark margin either side and read as a
                        // misaligned button rather than a selected section.
                        width: '100%',
                        py: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        textDecoration: 'none',
                        color: isActive ? nav.textActive : nav.text,
                        bgcolor: isActive ? nav.active : 'transparent',
                        '&:hover': {
                          bgcolor: isActive ? nav.active : nav.hover,
                          color: nav.textActive,
                        },
                        '& svg': { fontSize: 26 },
                        // A bar flush to the rail's left edge, running the
                        // full height of the row, so the whole thing reads
                        // as one selected block.
                        ...(isActive && {
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0, top: 0, bottom: 0,
                            width: 3,
                            bgcolor: nav.accent,
                          },
                        }),
                      }}
                    >
                      {section.icon}
                      <Typography
                        component="span"
                        sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.1 }}
                      >
                        {section.label}
                      </Typography>
                    </Box>
                );
              })}
            </Box>
            )}

            {/* --------------------------------------------------- panel */}
            {!isPhone && (
            <Box
              component="aside"
              aria-label={active ? `${active.label} navigation` : 'Section navigation'}
              sx={{
                width: layout.panelWidth,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                borderRight: '1px solid',
                borderColor: 'divider',
                px: 1.5,
                pt: 2,
                pb: 2,
                overflowY: 'auto',
              }}
            >
              {/* One slot, mounted here on a desktop and in the drawer on a
                  phone. Two would mean the portal picking a winner. */}
              <Box ref={setSlot} sx={{ display: 'contents' }} />
            </Box>
            )}

            {/* ---------------------------------------------------- page */}
            <Box
              component="main"
              // The scrollbar's room is always kept, so a page long enough
              // to scroll is not narrower than one that is not (Drivers &
              // Staff was, beside every other folder).
              sx={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden', bgcolor: 'background.default' }}
            >
              {Array.from(kept.current.entries()).map(([section, page]) => {
                const shown = section === current;
                return (
                  <Box
                    key={section}
                    aria-hidden={shown ? undefined : true}
                    // inert: a hidden page takes no focus, clicks or keys.
                    inert={!shown}
                    sx={{
                      position: 'absolute', inset: 0, overflow: 'auto', scrollbarGutter: 'stable',
                      visibility: shown ? 'visible' : 'hidden', zIndex: shown ? 1 : 0,
                    }}
                  >
                    <SectionActiveContext.Provider value={shown}>
                      <LocationContext.Provider value={{ location: page.location, navigationType }}>
                        {/* Keyed so navigating away from a crashed screen
                            clears the boundary instead of stranding you on
                            the error until a reload — on the screen, not
                            the URL, for screens that select within
                            themselves (see boundaryKey). */}
                        <ErrorBoundary key={page.boundary}>
                          {page.element}
                        </ErrorBoundary>
                      </LocationContext.Provider>
                    </SectionActiveContext.Provider>
                  </Box>
                );
              })}
            </Box>

            {/* -------------------------------------------- phone drawer */}
            <Drawer
              open={isPhone && navOpen}
              onClose={() => setNavOpen(false)}
              ModalProps={{ keepMounted: true }}
              slotProps={{ paper: { sx: { width: 296, bgcolor: 'background.paper' } } }}
            >
              <Box sx={{ bgcolor: nav.bgRail, py: 1 }}>
                {visible.map((section) => {
                  const isActive = active?.to === section.to;
                  return (
                    <Box
                      key={section.to}
                      component={Link}
                      to={hrefFor(section)}
                      aria-current={isActive ? 'page' : undefined}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.75,
                        px: 2.5,
                        py: 1.6,
                        textDecoration: 'none',
                        color: isActive ? nav.textActive : nav.text,
                        bgcolor: isActive ? nav.active : 'transparent',
                        '& svg': { fontSize: 22 },
                        ...(isActive && {
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0, top: 0, bottom: 0, width: 3,
                            bgcolor: nav.accent,
                          },
                        }),
                      }}
                    >
                      {section.icon}
                      <Typography component="span" sx={{ fontSize: 15, fontWeight: 600 }}>
                        {section.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* The section's own navigation, which a phone had no way to
                  reach at all before: the vault's rosters and categories,
                  Fleet's filters, Operations' views. */}
              <Box sx={{ px: 1.5, py: 2, overflowY: 'auto' }}>
                {isPhone && <Box ref={setSlot} sx={{ display: 'contents' }} />}
              </Box>
            </Drawer>
            </Box>
          </Box>
        )}
      </PanelSlotProvider>
    </BreadcrumbProvider>
  );
};

export default AppShell;
